import React, { useState, useRef, useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { generateSlug } from "@/lib/utils";
import { blogPosts as dataBlogPosts } from "@/data/blogPosts";
import { Monitor, Users, Mail, Bot, Filter, MessageSquare, Star, BarChart, Settings, Check, X, ChevronDown, ChevronUp, TrendingUp, Megaphone, Share2, Code, Globe, Search, Volume2, VolumeX, CreditCard, Zap, Calendar, PieChart, Hash, ShoppingCart, MousePointerClick, LayoutTemplate, CalendarCheck, Home, Sparkles, Wrench, Scale, Briefcase, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadForm from "@/components/LeadForm";
import SEOHead from "@/components/SEOHead";

export const testimonials = [
  { name: "Sarah Jenkins", role: "Real Estate Broker", text: "Danu Marketing completely transformed our lead flow. We booked 14 new listings in the first month alone.", img: "https://i.pravatar.cc/150?img=47" },
  { name: "Marcus Thorne", role: "Agency Owner", text: "The AI bot handles 80% of our inbound inquiries now. My sales team only talks to qualified leads.", img: "https://i.pravatar.cc/150?img=11" },
  { name: "Elena Rodriguez", role: "Business Coach", text: "I was skeptical about 'done-for-you' systems, but they delivered a fully functioning funnel in 12 days.", img: "https://i.pravatar.cc/150?img=32" },
  { name: "David Chen", role: "Financial Advisor", text: "Our calendar is booked solid for the next 3 weeks. The CRM automations are worth their weight in gold.", img: "https://i.pravatar.cc/150?img=68" },
  { name: "Jessica Alba", role: "Med Spa Director", text: "We stopped paying for dead leads. The qualification process they built filters out all the tire-kickers.", img: "https://i.pravatar.cc/150?img=44" },
];

export const servicesData = [
  {
    icon: Monitor,
    title: "Websites That Actually Sell",
    desc: "Fast, mobile-first websites engineered to turn visitors into leads. Built to rank, designed to convert.",
    seoText: (
      <>
        <p>In today's digital landscape, a standard brochure website is no longer enough. Your website must function as a 24/7 sales representative, actively working to capture leads and drive revenue. Our custom website development services focus entirely on conversion rate optimization (CRO) and search engine visibility. We engineer high-performance, mobile-first websites that load in milliseconds, ensuring you never lose a prospect to slow load times.</p>
        <p>Every page we build is meticulously structured for local and national SEO, incorporating semantic HTML, schema markup, and strategic keyword placement to dominate search rankings. We integrate psychological design principles, compelling copywriting, and frictionless user experiences to guide visitors seamlessly from initial curiosity to booked appointments.</p>
        <p>Beyond aesthetics, our technical infrastructure ensures maximum uptime, robust security, and seamless integration with your CRM and marketing tools. Whether you're a local service provider, a medical spa, or a consulting firm, our websites are custom-tailored to establish industry authority, build immediate trust with your audience, and consistently generate a predictable pipeline of inbound leads.</p>
      </>
    )
  },
  {
    icon: Users,
    title: "Never Miss Another Lead",
    desc: "A CRM that captures every lead, organizes every conversation, and tells you exactly who to call next.",
    seoText: (
      <>
        <p>Managing leads through spreadsheets and scattered emails is a guaranteed way to lose revenue. Our comprehensive CRM implementation centralizes your entire sales pipeline, ensuring no opportunity slips through the cracks. We set up automated lead capture systems that instantly route inquiries from your website, social media, and ad campaigns directly into a unified, easy-to-manage dashboard.</p>
        <p>By leveraging advanced lead scoring and intelligent routing, your sales team can prioritize high-value prospects and close deals faster. Our CRM solutions provide complete visibility into the customer journey, tracking every interaction, email, and phone call in one place. This data-driven approach empowers you to make informed decisions, optimize your sales processes, and dramatically improve your closing rates.</p>
        <p>We customize the CRM architecture to match your specific business model, creating custom fields, pipelines, and reporting dashboards that give you real-time insights into your business health. Stop guessing where your leads are coming from and start managing your growth with precision and clarity.</p>
      </>
    )
  },
  {
    icon: MessageSquare,
    title: "Follow-Up On Autopilot",
    desc: "Smart workflows that nurture, follow up, and book appointments while you sleep — so no lead ever goes cold.",
    seoText: (
      <>
        <p>The fortune is in the follow-up, yet most businesses abandon leads after just one or two contact attempts. Our automated follow-up sequences solve this problem by deploying multi-channel touchpoints across email, SMS, and voicemail drops. We design intelligent workflows that nurture prospects over days, weeks, or months, keeping your brand top-of-mind until they are ready to buy.</p>
        <p>These automations are triggered by specific user behaviors—such as downloading a guide, abandoning a checkout, or requesting a quote—ensuring that every message is highly relevant and timely. By personalizing the communication based on the prospect's interests and actions, we significantly increase engagement and conversion rates.</p>
        <p>Our systems work tirelessly in the background, qualifying leads and prompting them to book appointments directly on your calendar. This allows your team to focus exclusively on speaking with warm, ready-to-buy prospects, eliminating the tedious and time-consuming process of manual follow-up and cold outreach.</p>
      </>
    )
  },
  {
    icon: Bot,
    title: "AI Bots That Book Clients",
    desc: "24/7 AI chat & voice agents that answer questions, qualify leads, and book appointments — without you lifting a finger.",
    seoText: (
      <>
        <p>Modern consumers expect immediate answers, regardless of the time of day. Our advanced AI chatbots and voice agents provide instant, intelligent responses to your prospects 24/7/365. Trained specifically on your business data, FAQs, and brand voice, these AI assistants deliver a seamless and natural conversational experience that builds trust and engagement.</p>
        <p>Beyond simply answering questions, our AI bots are programmed to actively qualify leads, gather essential contact information, and overcome common objections. They can seamlessly integrate with your calendar to book appointments, schedule consultations, and process reservations directly within the chat interface, drastically reducing friction in the buyer's journey.</p>
        <p>By automating the initial stages of customer interaction, you significantly reduce customer support costs while simultaneously increasing your lead conversion rates. Whether deployed on your website, Facebook Messenger, or Instagram, our AI solutions ensure that every inquiry is handled promptly and professionally, turning casual browsers into committed clients.</p>
      </>
    )
  },
  {
    icon: Filter,
    title: "Funnels Built to Convert",
    desc: "Battle-tested sales funnels for lead gen, launches, webinars, and offers — wired into your CRM from day one.",
    seoText: (
      <>
        <p>A website informs, but a sales funnel converts. We architect high-converting sales funnels designed to guide prospects through a specific, optimized journey toward a single call-to-action. Whether you're launching a new service, hosting a webinar, or running a lead generation campaign, our funnels are meticulously engineered to maximize your return on ad spend (ROAS).</p>
        <p>We handle every aspect of the funnel build, from compelling copywriting and persuasive video sales letters (VSLs) to frictionless checkout pages and strategic upsell sequences. By utilizing A/B testing and data analytics, we continuously refine the funnel's performance, identifying and eliminating any drop-off points to ensure maximum conversion efficiency.</p>
        <p>Every funnel we build is fully integrated with your CRM and email marketing platform, ensuring that leads are instantly segmented and nurtured based on their behavior. Stop sending paid traffic to a generic homepage and start driving it to a targeted, conversion-focused funnel that predictably generates revenue and scales your business.</p>
      </>
    )
  },
  {
    icon: Mail,
    title: "Email & SMS That Convert",
    desc: "Inbox-landing campaigns that turn cold subscribers into repeat buyers — automated, personalized, profitable.",
    seoText: (
      <>
        <p>Email and SMS marketing remain the highest ROI channels for customer acquisition and retention. We develop comprehensive email and text message strategies that cut through the noise and land directly in your audience's primary inbox. Our campaigns are designed to build relationships, educate your prospects, and drive direct response sales.</p>
        <p>We implement advanced segmentation strategies, ensuring that your messages are highly targeted and relevant to each subscriber's specific interests and purchase history. From welcome sequences and abandoned cart reminders to promotional broadcasts and re-engagement campaigns, we automate the entire lifecycle marketing process to maximize customer lifetime value (LTV).</p>
        <p>Our team handles the technical setup to ensure optimal deliverability, protecting your domain reputation and ensuring your messages aren't lost in the spam folder. By combining persuasive copywriting with strategic timing and compelling offers, we turn your email list into a predictable and scalable revenue engine.</p>
      </>
    )
  },
  {
    icon: Star,
    title: "5-Star Reputation, On Autopilot",
    desc: "Automatically request reviews from happy customers, monitor every platform, and respond fast — trust grows itself.",
    seoText: (
      <>
        <p>In today's trust-based economy, your online reputation is your most valuable asset. A strong profile of 5-star reviews is the ultimate conversion catalyst, directly impacting your local SEO rankings and influencing purchasing decisions. We implement automated reputation management systems that proactively request reviews from your satisfied customers immediately after a successful transaction or appointment.</p>
        <p>Our software integrates directly with Google Business Profile, Yelp, Facebook, and industry-specific review sites, making it incredibly easy for clients to share their positive experiences. Simultaneously, we set up internal feedback loops that intercept negative reviews before they go public, giving you the opportunity to resolve issues privately and protect your brand image.</p>
        <p>We also provide centralized monitoring dashboards that alert you the moment a new review is posted, allowing for rapid, professional responses. By consistently generating authentic, positive reviews on autopilot, you establish unshakeable social proof, outrank your local competitors, and naturally attract a higher volume of premium clients.</p>
      </>
    )
  },
  {
    icon: BarChart,
    title: "Know What's Working",
    desc: "Clear dashboards that show what's bringing in money — and what to stop wasting time on. Real numbers, real fast.",
    seoText: (
      <>
        <p>You can't scale what you can't measure. Many businesses waste thousands of dollars on marketing simply because they don't know which campaigns are actually generating a positive return. We build customized, real-time analytics dashboards that aggregate data from your website, CRM, advertising platforms, and sales systems into one crystal-clear interface.</p>
        <p>Our reporting solutions provide deep visibility into your most critical Key Performance Indicators (KPIs), including Cost Per Lead (CPL), Customer Acquisition Cost (CAC), and overall Return on Investment (ROI). We implement advanced attribution modeling to track exactly which ads, keywords, and traffic sources are driving the highest quality leads and the most revenue.</p>
        <p>With this level of transparency, you can confidently eliminate underperforming campaigns and double down on the strategies that are actively growing your business. Stop relying on gut feelings and vanity metrics; let us provide the actionable data and insights you need to make intelligent, profitable decisions and scale with certainty.</p>
      </>
    )
  },
  {
    icon: Settings,
    title: "Ongoing Optimization",
    desc: "We don't just build it and leave. We monitor, tweak, and optimize your system every month to maximize ROI.",
    seoText: (
      <>
        <p>The digital landscape is constantly evolving, and a \"set it and forget it\" approach is a recipe for stagnation. True growth requires continuous refinement and adaptation. Our ongoing optimization services ensure that your website, funnels, and marketing campaigns are always operating at peak performance, consistently driving better results month over month.</p>
        <p>We conduct rigorous A/B testing on headlines, calls-to-action, landing page layouts, and ad creatives to incrementally improve your conversion rates. Our team continuously monitors search engine algorithm updates, adjusting your SEO strategy to maintain and improve your organic rankings. We also analyze user behavior recordings and heatmaps to identify and eliminate any friction points in the customer journey.</p>
        <p>As your dedicated growth partner, we hold monthly strategy reviews to analyze performance data, discuss new opportunities, and align our optimization efforts with your evolving business goals. By relentlessly pursuing improvement, we ensure that your marketing systems remain a powerful, compounding asset that drives sustainable, long-term growth.</p>
      </>
    )
  },
  {
    icon: TrendingUp,
    title: "Lead Generation",
    desc: "High-quality, exclusive leads delivered straight to your pipeline.",
    seoText: (
      <>
        <p>Stop fighting over shared leads that have been sold to five other competitors. We engineer proprietary lead generation systems that attract high-intent prospects exclusively to your business. By combining advanced audience targeting with irresistible offers, we ensure your sales team is only speaking with people actively looking for your services.</p>
        <p>Our multi-channel approach leverages search intent, social disruption, and retargeting to capture leads at every stage of the buying cycle. We don't just focus on volume; we obsess over lead quality. Every campaign is meticulously tracked and optimized to lower your cost-per-acquisition while maximizing your return on investment.</p>
        <p>Whether you need motivated sellers in real estate, high-ticket consulting clients, or local service appointments, our lead generation engines are custom-built to flood your CRM with qualified opportunities day in and day out.</p>
      </>
    )
  },
  {
    icon: Megaphone,
    title: "Omni-Channel Marketing",
    desc: "Comprehensive digital marketing strategies that dominate your local market.",
    seoText: (
      <>
        <p>Relying on a single traffic source is a dangerous game. We build resilient, omni-channel marketing ecosystems that surround your target audience wherever they spend their time online. From Google Ads and Meta to TikTok and LinkedIn, we deploy your message across the platforms that matter most to your specific industry.</p>
        <p>Our media buying team utilizes advanced algorithmic bidding, dynamic creative testing, and granular audience segmentation to ensure your ad spend is highly efficient. We craft compelling ad copy and striking visuals that stop the scroll, capture attention, and drive immediate action.</p>
        <p>By synchronizing your organic efforts with paid acquisition, we create a compounding effect that rapidly scales your brand awareness and establishes you as the undisputed authority in your market.</p>
      </>
    )
  },
  {
    icon: Share2,
    title: "Social Media Management",
    desc: "Build a loyal audience and turn followers into paying clients.",
    seoText: (
      <>
        <p>Your social media presence should be more than just a digital brochure; it should be a powerful community-building and revenue-generating asset. We take full control of your social channels, curating high-quality, engaging content that resonates with your ideal demographic and amplifies your brand voice.</p>
        <p>Our strategies go beyond vanity metrics like likes and followers. We focus on meaningful engagement, strategic community management, and driving targeted traffic back to your conversion funnels. By consistently publishing valuable insights, behind-the-scenes content, and client success stories, we foster deep trust with your audience.</p>
        <p>Whether it's Instagram, Facebook, LinkedIn, or Twitter, we tailor our approach to leverage the unique strengths of each platform, ensuring your brand remains top-of-mind and culturally relevant in your industry.</p>
      </>
    )
  },
  {
    icon: Code,
    title: "WordPress Development",
    desc: "Custom, scalable, and secure WordPress websites built for performance.",
    seoText: (
      <>
        <p>WordPress powers over 40% of the internet, but not all WordPress sites are created equal. We specialize in developing custom, high-performance WordPress ecosystems that are secure, blazingly fast, and incredibly easy for your team to manage. We don't rely on bloated templates; we build bespoke themes tailored entirely to your brand's unique specifications.</p>
        <p>Our development process prioritizes clean code architecture, advanced caching mechanisms, and rigorous security protocols to protect your business and your users. We seamlessly integrate complex functionalities—from e-commerce and membership portals to advanced CRM connections and API integrations.</p>
        <p>With our WordPress solutions, you get absolute ownership of your digital real estate, unparalleled scalability, and a robust foundation that can grow infinitely alongside your business.</p>
      </>
    )
  },
  {
    icon: Globe,
    title: "Wix Website Design",
    desc: "Stunning, easy-to-manage Wix websites tailored to your brand.",
    seoText: (
      <>
        <p>For businesses seeking a beautiful, streamlined web presence with an intuitive backend, our Wix development services provide the perfect solution. We leverage the full power of the Wix platform, including Velo by Wix, to create visually striking websites that don't compromise on functionality or user experience.</p>
        <p>We push the boundaries of what's possible on Wix, designing custom animations, dynamic CMS collections, and seamless third-party app integrations. Our designs are pixel-perfect across all devices, ensuring your brand looks impeccable whether viewed on a desktop or a smartphone.</p>
        <p>Once the site is launched, we provide comprehensive training, empowering you to effortlessly update content, manage bookings, and process sales directly from your intuitive Wix dashboard without needing to write a single line of code.</p>
      </>
    )
  },
  {
    icon: Search,
    title: "Search Engine Optimization (SEO)",
    desc: "Rank at the top of Google and capture high-intent organic traffic.",
    seoText: (
      <>
        <p>Visibility is everything. If your business isn't on the first page of Google, you are losing market share to your competitors every single day. Our comprehensive SEO strategies are designed to secure top rankings for the high-intent keywords that drive actual revenue, not just empty traffic.</p>
        <p>We execute deep technical SEO audits, optimizing your site architecture, load speeds, and mobile responsiveness to satisfy Google's core web vitals. Our content team produces authoritative, long-form assets that naturally attract backlinks, while our local SEO specialists ensure your Google Business Profile dominates the local map pack.</p>
        <p>SEO is a long-term investment, and we provide total transparency throughout the process. With our detailed monthly reporting, you'll see exactly how your organic visibility is growing, how your domain authority is increasing, and most importantly, how that traffic is converting into booked clients.</p>
      </>
    )
  }
];

export const blogPosts = dataBlogPosts;

export const compareFeatures = [
  {
    category: "Website & Presence",
    items: [
      { name: "Custom Website Build", starter: true, growth: true, scale: true, tooltip: "Fully custom design built from scratch, no generic templates." },
      { name: "SEO Optimization", starter: "Basic", growth: "Advanced", scale: "Enterprise", tooltip: "Comprehensive on-page, technical, and local SEO to rank higher." },
      { name: "Google Business Profile", starter: true, growth: true, scale: true, tooltip: "Optimization and management of your local map pack listing." },
      { name: "Custom Sales Funnels", starter: false, growth: "1 Included", scale: "Unlimited", tooltip: "Dedicated landing pages designed specifically to convert traffic into leads." },
    ]
  },
  {
    category: "CRM & Automation",
    items: [
      { name: "CRM Implementation", starter: "Basic", growth: "Full Setup", scale: "Custom", tooltip: "Setup and configuration of your customer relationship management system." },
      { name: "Lead Capture Forms", starter: true, growth: true, scale: true, tooltip: "High-converting forms to capture lead data from your website." },
      { name: "Automated Email Nurture", starter: false, growth: true, scale: true, tooltip: "Automatic email sequences to keep your leads engaged." },
      { name: "Automated SMS Follow-up", starter: false, growth: true, scale: true, tooltip: "Instant text messages to new leads for immediate engagement." },
      { name: "AI Chatbot", starter: false, growth: true, scale: true, tooltip: "24/7 AI-powered chat to answer questions and qualify leads." },
      { name: "AI Voice Agents", starter: false, growth: false, scale: true, tooltip: "AI phone agents to handle inbound and outbound calls." },
    ]
  },
  {
    category: "Marketing & Growth",
    items: [
      { name: "Paid Ads Management", starter: false, growth: "1 Platform", scale: "Multi-Channel", tooltip: "Management of Google, Meta, or TikTok advertising campaigns." },
      { name: "Review Management", starter: true, growth: true, scale: true, tooltip: "Automated systems to request and manage customer reviews." },
      { name: "Monthly Strategy Calls", starter: "1 per month", growth: "2 per month", scale: "Weekly", tooltip: "Regular meetings to review performance and adjust strategy." },
      { name: "Detailed Analytics", starter: true, growth: true, scale: true, tooltip: "Comprehensive reporting on your marketing performance and ROI." },
    ]
  },
  {
    category: "Support & Maintenance",
    items: [
      { name: "Hosting & Security", starter: true, growth: true, scale: true, tooltip: "Secure, high-performance hosting for your website." },
      { name: "Ongoing Optimization", starter: true, growth: true, scale: true, tooltip: "Regular tweaks to your system to improve performance." },
      { name: "Priority Support", starter: "Email", growth: "Slack/Phone", scale: "Dedicated", tooltip: "Faster response times and more direct access to our team." },
    ]
  }
];

export const caseStudiesData = [
  {
    title: "Real Estate Brokerage",
    niche: "Real Estate",
    service: "Websites That Actually Sell",
    stat: "14 Listings",
    statDesc: "In first 30 days",
    challenge: "Spending $4k/mo on shared Zillow leads with 0% close rate.",
    solution: "Custom listing funnel + AI qualification bot + automated SMS follow-up.",
    result: "Generated 182 exclusive leads, booked 22 appointments, closed 14 listings.",
    image: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png"
  },
  {
    title: "Luxury Med Spa",
    niche: "Medical Spas",
    service: "Funnels Built to Convert",
    stat: "412% ROI",
    statDesc: "On Meta Ad spend",
    challenge: "High no-show rate and low-quality leads from Instagram boosting.",
    solution: "Frictionless booking funnel with deposit capture and SMS reminders.",
    result: "No-show rate dropped from 45% to 8%. Generated $38k in new revenue in 60 days.",
    image: "https://vibe.filesafe.space/1779537497488192178/assets/734ab4c2-4cd7-4094-8d08-2b80745db426.png"
  },
  {
    title: "HVAC Contractor",
    niche: "Home Services",
    service: "Search Engine Optimization (SEO)",
    stat: "3x Volume",
    statDesc: "In inbound calls",
    challenge: "Invisible on Google Maps. Relying on expensive HomeAdvisor leads.",
    solution: "Local SEO overhaul + Google Business Profile automation.",
    result: "Ranked #1 in 5 target zip codes. Monthly inbound calls tripled without ad spend.",
    image: "https://vibe.filesafe.space/1779537497488192178/assets/cc1fec8d-4a92-4a62-bdbb-81af9128fedc.png"
  }
];

export const industriesData = [
  {
    title: "Real Estate",
    icon: Home,
    desc: "Automated listing funnels and seller lead qualification systems.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>In the competitive real estate market, relying on shared leads from massive portals is a race to the bottom. We build proprietary real estate lead generation systems that attract high-intent buyers and motivated sellers directly to your brokerage.</p>
        <p>Through hyper-targeted local SEO for realtors and advanced social media advertising, we position your agents as the go-to neighborhood experts. Our custom property listing funnels and automated buyer qualification bots ensure your team spends time closing deals, not chasing cold prospects.</p>
      </>
    )
  },
  {
    title: "Medical Spas",
    icon: Sparkles,
    desc: "Frictionless booking funnels, deposit captures, and automated appointment reminders.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>Scaling a medical aesthetics practice requires a steady stream of premium patients seeking high-margin treatments. Our med spa marketing strategies are engineered to dominate local search results for lucrative services like Botox, dermal fillers, and laser therapies.</p>
        <p>We deploy frictionless booking funnels, automated patient nurture sequences, and deposit-capture systems to virtually eliminate no-shows. By elevating your clinic's online authority and patient acquisition processes, we turn casual website visitors into loyal, recurring clientele.</p>
      </>
    )
  },
  {
    title: "Home Services",
    icon: Wrench,
    desc: "Local SEO dominance for plumbers, roofers, and HVAC contractors.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>For home service contractors, visibility in the local map pack is the difference between a thriving business and a struggling one. We specialize in local SEO for contractors, ensuring your business appears at the top when homeowners search for emergency repairs or major installations.</p>
        <p>Our systems automate review generation to build unshakeable social proof and implement instant SMS responses to ensure you are the first contractor to speak with a new lead. We build high-performance websites that load instantly on mobile, making it easy for customers to book your services from the field.</p>
      </>
    )
  },
  {
    title: "Law Firms",
    icon: Scale,
    desc: "Authority-building content and automated client intake for legal practices.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>In the legal industry, trust and authority are paramount. We develop comprehensive digital marketing strategies for law firms that establish your attorneys as thought leaders in their respective practice areas. Through high-quality content marketing and authoritative backlink acquisition, we drive high-value cases directly to your firm.</p>
        <p>Our automated intake systems qualify potential clients instantly, ensuring your legal team only spends time on viable cases. We also implement sophisticated CRM workflows to manage client relationships and ensure no communication falls through the cracks during long litigation cycles.</p>
      </>
    )
  },
  {
    title: "E-Commerce",
    icon: ShoppingCart,
    desc: "High-converting sales funnels and automated email/SMS retargeting for brands.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>Scaling an e-commerce brand requires moving beyond basic storefronts and embracing high-converting sales funnels. We architect bespoke customer journeys that maximize average order value (AOV) through strategic upsells and cross-sells. Our data-driven approach to paid media ensures your ad spend is always generating a positive ROAS.</p>
        <p>We implement aggressive behavioral retargeting across email and SMS to recover abandoned carts and nurture repeat purchases. By optimizing every stage of the digital commerce funnel, we help boutique brands compete with industry giants and build sustainable, recurring revenue streams.</p>
      </>
    )
  },
  {
    title: "Coaches & Consultants",
    icon: Briefcase,
    desc: "Personal branding, VSL funnels, and automated appointment setting.",
    beforeImage: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    afterImage: "https://vibe.filesafe.space/1779537497488192178/assets/e22e1008-d9f0-40c7-9c7c-900433739f3d.png",
    seoText: (
      <>
        <p>For coaches and consultants, you are the product. We build powerful personal branding ecosystems that showcase your expertise and attract high-ticket clients. Our high-fidelity Video Sales Letter (VSL) funnels educate your prospects and pre-sell them on your methodology before they ever hop on a call.</p>
        <p>We deploy automated appointment setting systems and AI-powered qualification bots to manage your calendar and ensure you are only speaking with qualified, ready-to-buy prospects. By automating the lead acquisition and nurturing process, we free you to focus on delivering world-class results for your clients.</p>
      </>
    )
  }
];

export const faqs = [
  {
    question: "How long does it take to see results?",
    answer: "Our systems are built for speed. Most clients see their new website and CRM live within 14 days. While SEO is a long-term strategy that compounds over 3–6 months, our automated follow-up and paid ad systems can start generating and booking leads within the first 48 hours of going live."
  },
  {
    question: "Do I have to sign a long-term contract?",
    answer: "No. We believe in earning your business every single month. We offer month-to-month agreements with no hidden cancellation fees. We also provide a 30-day money-back guarantee on our setup fee because we are that confident in our growth systems."
  },
  {
    question: "Will this work with my existing tools?",
    answer: "Almost certainly. We specialize in integrations. Whether you use WordPress, Shopify, Calendly, Stripe, or industry-specific software, we can usually connect everything into one unified growth engine using Zapier or custom API connections."
  },
  {
    question: "What makes Danu Marketing different from other agencies?",
    answer: "Most agencies sell you 'activities' (like posting on social media or building a site). We sell 'outcomes' (booked appointments and revenue). We build the entire engine—the site, the CRM, the AI, and the ads—and then we tune it every month based on real data until your numbers go up."
  },
  {
    question: "Is the AI chatbot hard to manage?",
    answer: "Not at all. We handle the entire setup, training, and optimization of your AI agents. You simply receive the booked appointments on your calendar and the qualified lead data in your CRM. It's designed to save you time, not add more work to your plate."
  }
];

export const videoTestimonials = [
  {
    title: "How we scaled a local law firm by 300%",
    thumbnail: "https://vibe.filesafe.space/1779537497488192178/assets/e1cc1b39-60d8-4d99-b024-1e9b0506e90c.png",
    duration: "2:45"
  },
  {
    title: "Automating lead flow for a national med spa chain",
    thumbnail: "https://vibe.filesafe.space/1779537497488192178/assets/734ab4c2-4cd7-4094-8d08-2b80745db426.png",
    duration: "3:12"
  },
  {
    title: "14 Listings in 30 days: A Real Estate Case Study",
    thumbnail: "https://vibe.filesafe.space/1779537497488192178/assets/0c8f40d7-1b5b-488d-999d-92734f16eaf1.png",
    duration: "4:05"
  }
];

export function ServiceCard({ service, onOpenForm }: { service: any, onOpenForm: () => void }) {
  return (
    <div className="group bg-background border border-primary/30 p-8 hover:border-accent hover:shadow-2xl transition-all duration-500 relative overflow-hidden flex flex-col h-full">
      <div className="absolute top-0 left-0 w-full h-1 bg-primary/10 group-hover:bg-accent transition-colors duration-500" />
      <div className="w-14 h-14 bg-primary/5 text-primary group-hover:bg-accent/10 group-hover:text-accent flex items-center justify-center mb-6 border border-primary/10 group-hover:border-accent/30 transition-all duration-500">
        <service.icon className="w-7 h-7" />
      </div>
      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{service.title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed mb-8 flex-1">{service.desc}</p>
      <div className="mt-auto space-y-4">
        <Link to={`/services/${generateSlug(service.title)}`} className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:text-accent transition-colors uppercase tracking-wider group/link">
          Learn More <ChevronUp className="w-4 h-4 rotate-90 group-hover/link:translate-x-1 transition-transform" />
        </Link>
        <Button 
          variant="outline" 
          className="w-full border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-none"
          onClick={onOpenForm}
        >
          Get Started
        </Button>
      </div>
    </div>
  );
}

export function IndustryCard({ industry, onOpenForm }: { industry: any, onOpenForm: () => void }) {
  return (
    <div className="group bg-background border border-primary/30 p-8 hover:border-accent hover:shadow-2xl transition-all duration-500 relative overflow-hidden flex flex-col h-full">
      <div className="absolute top-0 left-0 w-full h-1 bg-primary/10 group-hover:bg-accent transition-colors duration-500" />
      <div className="w-14 h-14 bg-primary/5 text-primary group-hover:bg-accent/10 group-hover:text-accent flex items-center justify-center mb-6 border border-primary/10 group-hover:border-accent/30 transition-all duration-500">
        <industry.icon className="w-7 h-7" />
      </div>
      <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{industry.title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed mb-8 flex-1">{industry.desc}</p>
      <div className="mt-auto space-y-4">
        <Link to={`/industries/${generateSlug(industry.title)}`} className="inline-flex items-center gap-2 text-xs font-bold text-primary hover:text-accent transition-colors uppercase tracking-wider group/link">
          View Solution <ChevronUp className="w-4 h-4 rotate-90 group-hover/link:translate-x-1 transition-transform" />
        </Link>
        <Button 
          variant="outline" 
          className="w-full border-primary/20 text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-none"
          onClick={onOpenForm}
        >
          See Case Study
        </Button>
      </div>
    </div>
  );
}

export function CaseStudyCard({ study, onOpenForm }: { study: any, onOpenForm: () => void }) {
  return (
    <div className="group bg-background border border-primary/30 hover:border-accent transition-all duration-500 relative overflow-hidden flex flex-col shadow-lg">
      <div className="relative aspect-video overflow-hidden border-b border-primary/30">
        <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
        <img src={study.image} alt={study.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute top-4 left-4 z-20 bg-background border border-primary/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-accent">
          {study.niche}
        </div>
      </div>
      <div className="p-8 flex flex-col flex-1">
        <div className="mb-6">
          <p className="text-3xl font-extrabold text-primary mb-1">{study.stat}</p>
          <p className="text-xs font-bold text-accent uppercase tracking-widest">{study.statDesc}</p>
        </div>
        <h3 className="text-xl font-bold mb-4">{study.title}</h3>
        <div className="space-y-4 mb-8 flex-1">
          <div>
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Challenge</p>
            <p className="text-sm text-foreground/80 leading-relaxed">{study.challenge}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-1">Result</p>
            <p className="text-sm text-foreground font-medium leading-relaxed">{study.result}</p>
          </div>
        </div>
        <Button 
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-none py-6"
          onClick={onOpenForm}
        >
          View Full Case Study
        </Button>
      </div>
    </div>
  );
}

export function VideoCarousel({ items }: { items: any[] }) {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const next = () => setActive((prev) => (prev + 1) % items.length);
  const prev = () => setActive((prev) => (prev - 1 + items.length) % items.length);

  useEffect(() => {
    if (scrollRef.current) {
      const scrollAmount = active * (scrollRef.current.offsetWidth / 1);
      scrollRef.current.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [active]);

  return (
    <div className="relative group">
      <div ref={scrollRef} className="flex overflow-hidden snap-x snap-mandatory no-scrollbar">
        {items.map((item, i) => (
          <div key={i} className="w-full shrink-0 snap-center px-5">
            <div className="max-w-4xl mx-auto bg-primary text-primary-foreground border border-primary/30 shadow-2xl relative overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative aspect-video md:aspect-auto h-full overflow-hidden group/video">
                  <div className="absolute inset-0 bg-primary/40 group-hover/video:bg-primary/20 transition-colors z-10" />
                  <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="w-16 h-16 bg-accent text-primary flex items-center justify-center rounded-full shadow-2xl group-hover/video:scale-110 transition-transform">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="m7 4 12 8-12 8V4z"/></svg>
                    </div>
                  </div>
                  <div className="absolute bottom-4 right-4 z-20 bg-background/80 backdrop-blur px-2 py-1 text-[10px] font-bold text-primary">
                    {item.duration}
                  </div>
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex gap-1 mb-6">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-accent text-accent" />)}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">"{item.title}"</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-accent/20 rounded-full border border-accent/30 flex items-center justify-center">
                      <Users className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">Verified Partner</p>
                      <p className="text-sm text-primary-foreground/60 uppercase tracking-widest">Client Success Story</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-2 mt-8">
        {items.map((_, i) => (
          <button key={i} onClick={() => setActive(i)} className={`w-12 h-1 transition-all duration-500 ${active === i ? 'bg-accent' : 'bg-primary/20'}`} aria-label={`Go to slide ${i + 1}`} />
        ))}
      </div>

      <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/80 backdrop-blur border border-primary/30 flex items-center justify-center hover:bg-primary hover:text-accent transition-colors text-primary shadow-xl opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex" aria-label="Previous">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-background/80 backdrop-blur border border-primary/30 flex items-center justify-center hover:bg-primary hover:text-accent transition-colors text-primary shadow-xl opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex" aria-label="Next">
        <ChevronRight className="w-6 h-6" />
      </button>
    </div>
  );
}

function ChevronLeft(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
}

function ChevronRight(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
}

export function AnimatedPipeline() {
  return (
    <div className="relative w-full h-full min-h-[400px] bg-primary border border-primary/30 p-6 sm:p-10 flex flex-col justify-between overflow-hidden shadow-2xl">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(184,151,90,0.15),transparent_70%)] pointer-events-none" />
      
      <div className="absolute left-[3.25rem] sm:left-[4.5rem] top-16 bottom-16 w-[2px] bg-primary-foreground/10">
        <div className="absolute top-0 left-1/2 w-[2px] h-24 bg-accent -translate-x-1/2 shadow-[0_0_15px_rgba(184,151,90,1)] animate-flow-down" />
      </div>

      <div className="relative z-10 flex flex-col gap-6 sm:gap-8 h-full justify-between">
        <PipelineNode icon={MousePointerClick} title="1. Traffic & Clicks" desc="Ads, SEO, & Social Media" />
        <PipelineNode icon={LayoutTemplate} title="2. Lead Capture" desc="High-converting funnel" />
        <PipelineNode icon={Bot} title="3. AI Qualification" desc="Instant SMS/Chat follow-up" />
        <PipelineNode icon={CalendarCheck} title="4. Booked Client" desc="Direct to your calendar" highlight />
      </div>
    </div>
  );
}

function PipelineNode({ icon: Icon, title, desc, highlight = false }: any) {
  return (
    <div className="flex items-center gap-5 sm:gap-6 group">
      <div className={`w-14 h-14 sm:w-16 sm:h-16 shrink-0 flex items-center justify-center border z-10 relative ${highlight ? 'bg-accent text-primary border-accent shadow-[0_0_30px_rgba(184,151,90,0.4)]' : 'bg-background text-accent border-primary/40 group-hover:border-accent transition-colors'}`}>
        <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
      </div>
      <div>
        <h3 className={`font-bold text-base sm:text-lg ${highlight ? 'text-accent' : 'text-primary-foreground'}`}>{title}</h3>
        <p className="text-xs sm:text-sm text-primary-foreground/60">{desc}</p>
      </div>
    </div>
  );
}

export function InteractiveQuiz({ onOpenForm }: { onOpenForm: () => void }) {
  const [step, setStep] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = [
    {
      title: "What is your primary business goal right now?",
      options: ["Generate more inbound leads", "Improve lead quality & conversion", "Automate my follow-up & save time", "Scale my existing successful campaigns"]
    },
    {
      title: "What is your biggest marketing bottleneck?",
      options: ["My website doesn't convert visitors", "I'm buying shared/low-quality leads", "My team takes too long to follow up", "I don't know my exact ROI/CPA"]
    },
    {
      title: "How many leads do you currently generate per month?",
      options: ["0 - 10", "11 - 50", "51 - 200", "200+"]
    },
    {
      title: "How do you currently follow up with leads?",
      options: ["Manual calls/emails", "Basic email autoresponder", "Full CRM with SMS/Email automation", "We don't have a system"]
    },
    {
      title: "What is your current monthly marketing budget?",
      options: ["Under $1,000", "$1,000 - $3,000", "$3,000 - $10,000", "$10,000+"]
    }
  ];

  const handleAnswer = (answer: string) => {
    setAnswers(prev => ({ ...prev, [step]: answer }));
    if (step < questions.length) {
      setStep(step + 1);
    }
  };

  const handleSubmitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch("https://services.leadconnectorhq.com/hooks/b5IpxWX8b85tEC05kVos/webhook-trigger/eb3a59f1-31e6-47f5-b08c-08f852159959", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: name.split(" ")[0],
          last_name: name.split(" ").slice(1).join(" "),
          email: email,
          primary_business_goal: answers[0],
          marketing_bottleneck: answers[1],
          leads_per_month: answers[2],
          current_follow_up: answers[3],
          monthly_marketing_budget: answers[4],
          source: "Interactive Quiz"
        })
      });
    } catch (error) {
      console.error("Error submitting quiz", error);
    }
    
    setIsSubmitting(false);
    setShowResult(true);
  };

  if (showResult) {
    return (
      <div className="bg-background border border-primary/30 p-8 md:p-12 shadow-2xl text-center animate-in fade-in zoom-in-95 duration-500">
        <div className="w-16 h-16 bg-accent/10 text-accent flex items-center justify-center mx-auto mb-6 border border-accent/30">
          <Sparkles className="w-8 h-8" />
        </div>
        <h3 className="text-2xl md:text-3xl font-bold mb-4">Your Custom Growth Blueprint is Ready</h3>
        <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
          Based on your answers, you need a high-converting automated funnel paired with an AI qualification bot. This will immediately solve your lead quality issues and save your team hours of manual follow-up.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 shadow-lg border border-primary rounded-none" onClick={onOpenForm}>
            Claim Your Free Strategy Audit
          </Button>
          <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold px-8 rounded-none" onClick={() => { setStep(0); setShowResult(false); setAnswers({}); }}>
            Retake Quiz
          </Button>
        </div>
      </div>
    );
  }

  if (step === questions.length) {
    return (
      <div className="bg-background border border-primary/30 p-8 md:p-12 shadow-2xl text-center animate-in fade-in slide-in-from-right-8 duration-500">
        <h3 className="text-2xl md:text-3xl font-bold mb-4">Analyzing Your Responses...</h3>
        <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
          We're preparing your custom growth blueprint. Where should we send your results?
        </p>
        <form onSubmit={handleSubmitLead} className="max-w-md mx-auto space-y-4 text-left">
          <div>
            <label htmlFor="quiz-name" className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Full Name</label>
            <input 
              id="quiz-name"
              type="text" 
              required
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full bg-background border border-primary/30 p-4 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label htmlFor="quiz-email" className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Email Address</label>
            <input 
              id="quiz-email"
              type="email" 
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full bg-background border border-primary/30 p-4 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition-colors"
              placeholder="john@example.com"
            />
          </div>
          <Button type="submit" disabled={isSubmitting} size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold mt-4 rounded-none h-14">
            {isSubmitting ? "Processing..." : "Get My Results"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-background border border-primary/30 p-8 md:p-12 shadow-2xl relative overflow-hidden">
      <div className="flex items-center gap-4 mb-8">
        <div className="flex-1 h-1 bg-primary/10">
          <div 
            className="h-full bg-accent transition-all duration-500" 
            style={{ width: `${((step) / questions.length) * 100}%` }} 
          />
        </div>
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          Step {step + 1} of {questions.length}
        </span>
      </div>

      <div className="animate-in fade-in slide-in-from-right-8 duration-500" key={step}>
        <h3 className="text-2xl md:text-3xl font-bold mb-8">{questions[step].title}</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {questions[step].options.map((opt, i) => (
            <button 
              key={i}
              onClick={() => handleAnswer(opt)}
              className="p-6 text-left border border-primary/20 hover:border-accent hover:bg-accent/5 hover:shadow-md transition-all group focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <span className="block text-sm font-bold text-primary group-hover:text-accent mb-2 transition-colors">Option {String.fromCharCode(65 + i)}</span>
              <span className="block text-base font-medium text-foreground">{opt}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Index() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        const hasSeenPopup = sessionStorage.getItem('hasSeenExitIntent');
        if (!hasSeenPopup) {
          setShowExitIntent(true);
          sessionStorage.setItem('hasSeenExitIntent', 'true');
        }
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);
  const [showMatrix, setShowMatrix] = useState(false);
  const [visitors, setVisitors] = useState(5000);
  const [conversionRate, setConversionRate] = useState(1.5);
  const [customerValue, setCustomerValue] = useState(1500);
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", ...Array.from(new Set(blogPosts.map(post => post.category)))];
  const filteredPosts = activeCategory === "All" ? blogPosts : blogPosts.filter(post => post.category === activeCategory);

  const currentRevenue = visitors * (conversionRate / 100) * customerValue;
  const projectedRevenue = visitors * ((conversionRate + 2.5) / 100) * customerValue;
  const missedRevenue = projectedRevenue - currentRevenue;

  const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  return (
    <div className="min-h-screen bg-background text-foreground font-sans overflow-x-hidden">
      <SEOHead 
        title="Get More Leads, Book More Clients"
        description="We build websites, CRMs, and AI bots that capture, qualify, and book your next 50 clients — while you focus on what you do best."
        canonical="/"
        preloadImage="https://vibe.filesafe.space/1779537497488192178/assets/da8a0ee4-e313-47af-b196-a2f698248cef.png"
      />
      <Header />

      <main>
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute top-[-80px] left-[-80px] w-[360px] h-[360px] bg-accent/20 blur-[80px] pointer-events-none" />
          <div className="absolute bottom-[-100px] right-[-60px] w-[320px] h-[320px] bg-primary/10 blur-[80px] pointer-events-none" />
          
          <div className="max-w-6xl mx-auto px-5 relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-8 items-stretch">
            <div className="text-left flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-accent/10 text-accent font-medium text-xs border border-accent/20 mb-6 uppercase tracking-wider">
                <span className="w-1.5 h-1.5 bg-accent animate-pulse" />
                Built for service businesses ready to scale
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
                Get more leads. Book more clients. <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">Work less.</span>
              </h1>
              
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-8">
                We build the website, CRM, and AI bots that capture, qualify, and book your next 50 clients — while you focus on actually running your business.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-[0_0_20px_rgba(184,151,90,0.4)] hover:shadow-[0_0_30px_rgba(184,151,90,0.6)] transition-all animate-pulse px-8" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}>Get My Free Growth Audit →</Button>
                <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8" onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}>See How It Works</Button>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 text-sm text-muted-foreground font-medium">
                <div className="flex -space-x-3">
                  <img src="https://i.pravatar.cc/100?img=11" alt="User" className="w-10 h-10 border-2 border-background rounded-full" />
                  <img src="https://i.pravatar.cc/100?img=32" alt="User" className="w-10 h-10 border-2 border-background rounded-full" />
                  <img src="https://i.pravatar.cc/100?img=44" alt="User" className="w-10 h-10 border-2 border-background rounded-full" />
                  <img src="https://i.pravatar.cc/100?img=68" alt="User" className="w-10 h-10 border-2 border-background rounded-full" />
                  <div className="w-10 h-10 rounded-full border-2 border-background bg-accent text-primary flex items-center justify-center text-[10px] font-bold z-10">+500</div>
                </div>
                <div className="flex flex-col">
                  <div className="flex text-accent mb-1">
                    <Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" />
                  </div>
                  <span className="text-foreground font-bold">Trusted by 500+ businesses</span>
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-md lg:max-w-none mt-8 lg:mt-0 animate-float h-full min-h-[300px] lg:min-h-[400px]">
              <div className="relative overflow-visible h-full">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent mix-blend-overlay z-10 pointer-events-none" />
                <img 
                  src="https://vibe.filesafe.space/1779537497488192178/assets/da8a0ee4-e313-47af-b196-a2f698248cef.png" 
                  alt="Business growth analytics and lead generation dashboard" 
                  className="w-full h-full object-cover relative z-0 drop-shadow-2xl"
                  fetchPriority="high"
                  loading="eager"
                />
              </div>

              <div className="absolute -bottom-4 -left-2 sm:-left-4 lg:-left-8 bg-background p-3 sm:p-4 shadow-xl border border-primary/30 flex items-center gap-3 sm:gap-4 z-20 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300 w-[90%] sm:w-auto max-w-[280px]">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 text-accent flex items-center justify-center shrink-0">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 fill-accent" />
                </div>
                <div>
                  <div className="flex gap-1 mb-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-accent text-accent" />)}
                  </div>
                  <p className="text-xs sm:text-sm font-bold text-foreground">5.0 Average Rating</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">From 200+ clients</p>
                </div>
              </div>

              <div className="absolute -top-4 -right-2 sm:-right-4 lg:-right-6 bg-background p-3 sm:p-4 shadow-xl border border-primary/30 flex items-center gap-3 sm:gap-4 z-20 animate-in fade-in slide-in-from-top-8 duration-700 delay-500 w-[85%] sm:w-auto max-w-[240px]">
                <div className="flex -space-x-3">
                  <img src="https://i.pravatar.cc/100?img=11" alt="User" loading="lazy" className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-background rounded-full" />
                  <img src="https://i.pravatar.cc/100?img=12" alt="User" loading="lazy" className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-background rounded-full" />
                  <img src="https://i.pravatar.cc/100?img=13" alt="User" loading="lazy" className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-background rounded-full" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-bold text-foreground">500+ Businesses</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">Scaled successfully</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-3 bg-primary border-b border-primary-foreground/10 overflow-hidden flex items-center">
          <div className="flex overflow-hidden group w-full">
            <div className="flex animate-marquee group-hover:[animation-play-state:paused] shrink-0 items-center">
              {testimonials.map((t, i) => (
                <div key={i} className="flex items-center gap-2 shrink-0 px-8 border-r border-primary-foreground/20">
                  <Star className="w-3.5 h-3.5 fill-accent text-accent shrink-0" />
                  <p className="text-sm font-medium text-primary-foreground whitespace-nowrap">"{t.text}"</p>
                  <span className="text-sm text-accent whitespace-nowrap">— {t.name}</span>
                </div>
              ))}
            </div>
            <div className="flex animate-marquee group-hover:[animation-play-state:paused] shrink-0 items-center" aria-hidden="true">
              {testimonials.map((t, i) => (
                <div key={`dup-${i}`} className="flex items-center gap-2 shrink-0 px-8 border-r border-primary-foreground/20">
                  <Star className="w-3.5 h-3.5 fill-accent text-accent shrink-0" />
                  <p className="text-sm font-medium text-primary-foreground whitespace-nowrap">"{t.text}"</p>
                  <span className="text-sm text-accent whitespace-nowrap">— {t.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 bg-secondary border-b overflow-hidden">
          <div className="max-w-6xl mx-auto px-5">
            <p className="text-center text-xs text-muted-foreground uppercase tracking-widest mb-6 font-medium">
              Helping 500+ service businesses book more clients
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60 font-semibold text-muted-foreground">
              <span>Coaches</span>
              <span>Real Estate</span>
              <span>Healthcare</span>
              <span>Agencies</span>
              <span>Local Services</span>
              <span>Consulting</span>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background overflow-hidden" id="services">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">What we build</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything you need to <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">turn strangers into paying clients</span></h2>
              <p className="text-muted-foreground">Most agencies sell you one thing and disappear. We build the whole growth engine — and tune it until your numbers go up.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
              {servicesData.map((service, i) => (
                <ServiceCard key={i} service={service} onOpenForm={() => setIsFormModalOpen(true)} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background overflow-hidden" id="compare">
          <div className="max-w-7xl mx-auto px-5">
            <div className="grid lg:grid-cols-2 gap-16 items-stretch">
              <div className="flex flex-col justify-center">
                <div className="mb-12">
                  <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">The difference</span>
                  <h2 className="text-3xl md:text-4xl font-bold mb-4">Most agencies sell you tools. <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">We sell you outcomes.</span></h2>
                  <p className="text-muted-foreground">Here's exactly what changes when you stop hiring vendors and start hiring a growth partner.</p>
                </div>

                <div className="bg-primary/20 overflow-hidden border border-primary/30 shadow-lg flex flex-col gap-[1px]">
                  <div className="grid grid-cols-2 gap-[1px] bg-primary/20">
                    <div className="bg-primary p-3 sm:p-4 text-center font-bold text-accent text-sm md:text-base">Danu Marketing</div>
                    <div className="bg-secondary p-3 sm:p-4 text-center font-bold text-muted-foreground text-sm md:text-base">Most agencies</div>
                  </div>
                  
                  {[
                    ["Build you a pretty website, hand you the keys, disappear", "Build your entire growth system and tune it monthly until it books clients"],
                    ["3–6 month builds. Endless revision rounds.", "Live in 14 days. Optimization starts day one."],
                    ["You juggle 5+ disconnected tools and subscriptions", "One integrated system: site, CRM, AI, automations — done."],
                    ["Outsourced support desk. 48-hour ticket replies.", "Direct line to our team. Same-day human replies."],
                    ["12-month contracts. Cancellation fees. No accountability.", "Month-to-month. 30-day money-back. You stay because you want to."],
                    ["You still have to figure out how to actually get clients", "We don't stop until you're booking the clients you set out to book"]
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-2 gap-[1px] bg-primary/20">
                      <div className="bg-secondary/50 p-3 sm:p-4 flex items-start gap-2 sm:gap-3 text-xs md:text-sm font-medium">
                        <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                        <span className="break-words">{row[1]}</span>
                      </div>
                      <div className="bg-background p-3 sm:p-4 flex items-start gap-2 sm:gap-3 text-xs md:text-sm text-muted-foreground">
                        <X className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                        <span className="break-words">{row[0]}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative h-full min-h-[300px] lg:min-h-[400px]">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent mix-blend-overlay z-10 pointer-events-none" />
                <img 
                  src="https://vibe.filesafe.space/1779537497488192178/assets/ef74f845-706e-4fbb-a332-ec46b60fbbb6.png" 
                  alt="Organized analytics dashboard" 
                  loading="lazy"
                  className="w-full h-full object-cover border border-primary/30 shadow-2xl relative z-0"
                />
                <div className="absolute -bottom-4 -left-2 sm:-left-6 bg-background border border-primary/30 p-3 sm:p-4 shadow-xl z-20 flex items-center gap-3 sm:gap-4 w-[85%] sm:w-auto max-w-[260px]">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-accent/10 flex items-center justify-center shrink-0">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-bold text-foreground">Predictable Growth</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Data-driven decisions</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-secondary border-t border-primary/10 overflow-hidden" id="industries">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Business Niches We Serve</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Specialized growth systems for <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">your exact niche</span></h2>
              <p className="text-muted-foreground">We don't do cookie-cutter. Every system is custom-architected for the buying behavior of your industry.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industriesData.map((industry, i) => (
                <IndustryCard key={i} industry={industry} onOpenForm={() => setIsFormModalOpen(true)} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background border-t border-primary/10 overflow-hidden" id="integrations">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Seamless Integrations</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Plays nice with <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">your favorite tools</span></h2>
              <p className="text-muted-foreground">We connect your new growth engine directly to the software you already use to run your business.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {[
                { name: "Stripe", icon: CreditCard },
                { name: "Zapier", icon: Zap },
                { name: "Google", icon: Search },
                { name: "Meta", icon: Share2 },
                { name: "Calendly", icon: Calendar },
                { name: "QuickBooks", icon: PieChart },
                { name: "Slack", icon: Hash },
                { name: "Shopify", icon: ShoppingCart },
              ].map((tool, i) => (
                <div key={i} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-5 bg-background border border-primary/30 hover:border-accent hover:shadow-lg transition-all group">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/5 text-primary group-hover:bg-accent/10 group-hover:text-accent flex items-center justify-center shrink-0 border border-primary/10 group-hover:border-accent/30 transition-colors">
                    <tool.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <span className="font-bold text-foreground group-hover:text-primary transition-colors text-base sm:text-lg">{tool.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background border-t border-primary/10 overflow-hidden" id="process">
          <div className="max-w-7xl mx-auto px-5">
            <div className="grid lg:grid-cols-2 gap-16 items-stretch">
              <div className="order-2 lg:order-1 relative h-full min-h-[300px] lg:min-h-[400px]">
                <AnimatedPipeline />
              </div>
              
              <div className="order-1 lg:order-2 flex flex-col justify-center">
                <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">The Process</span>
                <h2 className="text-3xl md:text-4xl font-bold mb-8">How we turn <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">traffic into revenue.</span></h2>
                
                <div className="space-y-8">
                  {[
                    { title: "1. Capture & Qualify", desc: "We build high-converting landing pages and deploy AI bots to instantly engage visitors, filter out tire-kickers, and capture real leads." },
                    { title: "2. Nurture & Automate", desc: "Leads are routed into a custom CRM where multi-channel workflows (email, SMS, voicemail) follow up relentlessly until they book." },
                    { title: "3. Close & Scale", desc: "Your sales team only talks to hot, qualified prospects. We track the data, optimize the funnel, and scale the campaigns that drive the highest ROI." }
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 bg-primary text-accent font-bold flex items-center justify-center shrink-0 border border-accent/30 text-lg">
                        {i + 1}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-secondary border-y border-primary/10 overflow-hidden" id="results">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Proven Results</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Don't just take our word for it. <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">Look at the math.</span></h2>
              <p className="text-muted-foreground">Real numbers from real service businesses that stopped guessing and started scaling with our systems.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {caseStudiesData.map((study, i) => (
                <CaseStudyCard key={i} study={study} onOpenForm={() => setIsFormModalOpen(true)} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background border-t border-primary/10 overflow-hidden" id="video-testimonials">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Client Stories</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Hear directly from our <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">successful partners</span></h2>
              <p className="text-muted-foreground">Watch how we've helped service businesses scale predictably.</p>
            </div>
          </div>
          <VideoCarousel items={videoTestimonials} />
        </section>

        <section className="py-16 md:py-24 bg-secondary border-t border-primary/10 overflow-hidden" id="quiz">
          <div className="max-w-4xl mx-auto px-5">
            <div className="text-center mb-12">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">System Matchmaker</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Find your perfect <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">growth system</span></h2>
              <p className="text-muted-foreground">Answer 3 quick questions to discover the exact blueprint you need to scale.</p>
            </div>
            <InteractiveQuiz onOpenForm={() => setIsFormModalOpen(true)} />
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background overflow-hidden" id="calculator">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Interactive ROI Calculator</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">How much revenue are you <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">leaving on the table?</span></h2>
              <p className="text-muted-foreground">Plug in your current numbers to see the exact revenue you're missing by using a low-converting website and manual follow-up.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-primary/30 shadow-2xl">
              <div className="p-8 md:p-12 bg-background border-b lg:border-b-0 lg:border-r border-primary/30 space-y-10">
                <h3 className="text-2xl font-bold mb-2">Your Current Metrics</h3>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Monthly Website Visitors</label>
                    <span className="text-2xl font-bold text-primary">{visitors.toLocaleString()}</span>
                  </div>
                  <input type="range" min="100" max="50000" step="100" value={visitors} onChange={(e) => setVisitors(Number(e.target.value))} className="dtg-slider" />
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Current Conversion Rate</label>
                    <span className="text-2xl font-bold text-primary">{conversionRate.toFixed(1)}%</span>
                  </div>
                  <input type="range" min="0.1" max="10" step="0.1" value={conversionRate} onChange={(e) => setConversionRate(Number(e.target.value))} className="dtg-slider" />
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <label className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Avg. Customer Value</label>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(customerValue)}</span>
                  </div>
                  <input type="range" min="100" max="10000" step="100" value={customerValue} onChange={(e) => setCustomerValue(Number(e.target.value))} className="dtg-slider" />
                </div>
              </div>

              <div className="p-8 md:p-12 bg-primary text-primary-foreground flex flex-col justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(184,151,90,0.15),transparent_50%)] pointer-events-none" />

                <div className="relative z-10 space-y-8">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70 mb-2">Current Monthly Revenue</p>
                    <p className="text-3xl font-bold">{formatCurrency(currentRevenue)}</p>
                  </div>

                  <div className="pt-6 border-t border-primary-foreground/10">
                    <p className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70 mb-2">Projected Revenue (with Danu Marketing)*</p>
                    <p className="text-4xl font-bold text-accent">{formatCurrency(projectedRevenue)}</p>
                  </div>

                  <div className="pt-6 border-t border-primary-foreground/10">
                    <p className="text-sm font-bold uppercase tracking-wider text-primary-foreground/70 mb-2">Money Left on the Table</p>
                    <p className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white tracking-tight">{formatCurrency(missedRevenue)} <span className="text-lg font-medium text-primary-foreground/70 tracking-normal">/mo</span></p>
                  </div>

                  <p className="text-xs text-primary-foreground/50 mt-4 leading-relaxed">*Based on a conservative +2.5% conversion rate increase from our high-converting funnels and automated follow-up.</p>

                  <Button 
                    size="lg" 
                    className="w-full mt-6 bg-accent hover:bg-accent/90 text-primary font-bold py-6 text-lg shadow-lg border border-accent rounded-none"
                    onClick={() => setIsFormModalOpen(true)}
                  >
                    Stop Losing Money →
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-secondary border-t border-primary/10 relative" id="pricing">
          <div className="max-w-7xl mx-auto px-5 relative">
            <div className="sticky top-24 z-30 flex justify-end mb-[-60px] pointer-events-none hidden md:flex">
              <Button 
                onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-accent hover:bg-accent/90 text-primary font-bold shadow-2xl border border-accent rounded-full px-6 py-6 pointer-events-auto flex items-center gap-2 hover:animate-none animate-bounce"
              >
                <PieChart className="w-5 h-5" />
                Calculate ROI
              </Button>
            </div>

            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Transparent Pricing</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Invest in a system that <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">pays for itself.</span></h2>
              <p className="text-muted-foreground">No hidden fees. No long-term contracts. Just scalable growth engines designed to multiply your revenue.</p>
              
              <div className="flex items-center justify-center gap-4 mt-8">
                <span className={`font-bold text-sm md:text-base transition-colors ${!isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>Monthly Billing</span>
                <button 
                  aria-label="Toggle annual billing"
                  onClick={() => setIsAnnual(!isAnnual)}
                  className="w-16 h-8 bg-background border border-primary/30 p-1 relative transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <div className={`w-6 h-6 bg-accent transition-transform duration-300 ${isAnnual ? 'translate-x-8' : 'translate-x-0'}`} />
                </button>
                <span className={`font-bold text-sm md:text-base flex items-center gap-2 transition-colors ${isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
                  Annual Billing <span className="bg-accent/10 text-accent text-[10px] uppercase tracking-wider px-2 py-0.5 border border-accent/20">Save 20%</span>
                </span>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 items-start mb-24">
              <div className="bg-background border border-primary/30 p-8 shadow-lg relative group hover:border-accent hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl transition-all duration-300 flex flex-col h-full">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary/20 group-hover:bg-primary transition-colors" />
                <h3 className="text-2xl font-bold mb-2">Starter</h3>
                <p className="text-sm text-muted-foreground mb-6 h-10">Perfect for local businesses needing a strong digital foundation.</p>
                <div className="mb-8">
                  <span className="text-4xl font-extrabold text-primary">{isAnnual ? "$1,200" : "$1,500"}</span>
                  <span className="text-muted-foreground">/month</span>
                  {isAnnual && <p className="text-xs text-muted-foreground mt-2">Billed $14,400 annually</p>}
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mb-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-none"
                  onClick={() => setIsFormModalOpen(true)}
                >
                  Get Started
                </Button>
                <div className="space-y-4 flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">What's included</p>
                  {[
                    "High-Converting Website Build",
                    "Basic CRM Setup",
                    "Google Business Profile Optimization",
                    "Automated Review Requests",
                    "Standard Email Support"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                  {[
                    "AI Chat & Voice Agents",
                    "Custom Sales Funnels",
                    "Paid Ads Management"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm text-muted-foreground/50">
                      <X className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary border-2 border-accent p-8 shadow-[0_0_40px_rgba(184,151,90,0.15)] relative flex flex-col h-full transform lg:-translate-y-4 hover:lg:-translate-y-6 hover:-translate-y-2 hover:scale-[1.02] hover:shadow-[0_0_60px_rgba(184,151,90,0.3)] transition-all duration-300">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-accent text-primary text-xs font-bold uppercase tracking-wider px-4 py-1 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> Most Popular
                </div>
                <h3 className="text-2xl font-bold mb-2 text-primary-foreground mt-4">Growth Engine</h3>
                <p className="text-sm text-primary-foreground/70 mb-6 h-10">The complete automated lead generation and booking system.</p>
                <div className="mb-8">
                  <span className="text-4xl font-extrabold text-accent">{isAnnual ? "$2,400" : "$3,000"}</span>
                  <span className="text-primary-foreground/70">/month</span>
                  {isAnnual && <p className="text-xs text-primary-foreground/50 mt-2">Billed $28,800 annually</p>}
                </div>
                <Button 
                  className="w-full mb-6 bg-accent hover:bg-accent/90 text-primary font-bold rounded-none shadow-lg border border-accent"
                  onClick={() => setIsFormModalOpen(true)}
                >
                  Claim Your Free Audit
                </Button>
                <div className="space-y-4 flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-accent mb-4">Everything in Starter, plus:</p>
                  {[
                    "AI Chatbot Qualification",
                    "Multi-Channel Automated Follow-up",
                    "1 Custom Sales Funnel (VSL/Webinar)",
                    "Google OR Meta Ads Management",
                    "Advanced Analytics Dashboard",
                    "Priority Support (Slack/Phone)"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm text-primary-foreground">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background border border-primary/30 p-8 shadow-lg relative group hover:border-accent hover:-translate-y-2 hover:scale-[1.02] hover:shadow-2xl transition-all duration-300 flex flex-col h-full">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary/20 group-hover:bg-primary transition-colors" />
                <h3 className="text-2xl font-bold mb-2">Scale</h3>
                <p className="text-sm text-muted-foreground mb-6 h-10">Omni-channel dominance for businesses ready to aggressively expand.</p>
                <div className="mb-8">
                  <span className="text-4xl font-extrabold text-primary">Custom</span>
                  <span className="text-muted-foreground"> Pricing</span>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mb-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold rounded-none"
                  onClick={() => setIsFormModalOpen(true)}
                >
                  Talk to Sales
                </Button>
                <div className="space-y-4 flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">Everything in Growth, plus:</p>
                  {[
                    "Omni-Channel Ads (Google, Meta, TikTok)",
                    "AI Voice Agents (Inbound/Outbound)",
                    "Unlimited Custom Funnels",
                    "Bespoke API Integrations",
                    "Dedicated Account Director",
                    "Weekly Strategy Calls"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Check className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-8">
                <Button 
                  variant="outline" 
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold px-8 rounded-none transition-colors"
                  onClick={() => setShowMatrix(!showMatrix)}
                >
                  {showMatrix ? 'Hide Full Feature Matrix' : 'Compare Plans in Detail'}
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 ${showMatrix ? 'rotate-180' : ''}`} />
                </Button>
              </div>
              <div className={`transition-all duration-500 ease-in-out overflow-hidden ${showMatrix ? 'max-h-[2500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="overflow-x-auto pb-8">
                  <div className="min-w-[800px] border border-primary/30 bg-background shadow-lg">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-primary/30">
                        <th className="p-5 font-bold w-1/3 text-lg bg-secondary">Features</th>
                        <th className="p-5 font-bold text-center w-[22%] text-lg bg-secondary">Starter</th>
                        <th className="p-5 font-bold text-center w-[22%] text-lg text-accent bg-primary/10">Growth Engine</th>
                        <th className="p-5 font-bold text-center w-[22%] text-lg bg-secondary">Scale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {compareFeatures.map((section, idx) => (
                        <Fragment key={idx}>
                          <tr className="bg-primary/10 border-y border-primary/30">
                            <td colSpan={4} className="p-4 font-bold text-primary uppercase text-xs tracking-wider">
                              {section.category}
                            </td>
                          </tr>
                          {section.items.map((item, itemIdx) => (
                            <tr key={itemIdx} className="border-b border-primary/10 hover:bg-secondary/30 transition-colors">
                              <td className="p-4 text-sm font-medium border-r border-primary/5">
                                <div className="flex items-center gap-2">
                                  {item.name}
                                  {item.tooltip && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help transition-colors" />
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-primary text-primary-foreground border-primary max-w-xs">
                                        <p className="text-sm">{item.tooltip}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                              </td>
                              <td className="p-4 text-center text-sm border-r border-primary/5">
                                {typeof item.starter === 'boolean' ? (item.starter ? <Check className="w-5 h-5 mx-auto text-accent" /> : <X className="w-5 h-5 mx-auto text-muted-foreground/30" />) : item.starter}
                              </td>
                              <td className="p-4 text-center text-sm font-medium bg-primary/5 border-r border-primary/5">
                                {typeof item.growth === 'boolean' ? (item.growth ? <Check className="w-5 h-5 mx-auto text-accent" /> : <X className="w-5 h-5 mx-auto text-muted-foreground/30" />) : item.growth}
                              </td>
                              <td className="p-4 text-center text-sm">
                                {typeof item.scale === 'boolean' ? (item.scale ? <Check className="w-5 h-5 mx-auto text-accent" /> : <X className="w-5 h-5 mx-auto text-muted-foreground/30" />) : item.scale}
                              </td>
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-background overflow-hidden" id="resources">
          <div className="max-w-6xl mx-auto px-5">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Resources & Insights</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Latest growth <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">strategies</span></h2>
              <p className="text-muted-foreground">Actionable advice and deep dives into the tactics working right now in digital marketing.</p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wider border transition-colors ${activeCategory === cat ? 'bg-primary text-accent border-primary' : 'bg-background text-muted-foreground border-primary/20 hover:border-accent hover:text-foreground'}`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, i) => (
                <Link key={i} to={`/blog/${post.slug}`} className="group flex flex-col bg-background border border-primary/30 hover:border-accent hover:shadow-xl transition-all duration-300 relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-accent">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500 z-10" />
                  <div className="relative aspect-video overflow-hidden border-b border-primary/30">
                    <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors z-10 pointer-events-none" />
                    <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-4 left-4 z-20 bg-background border border-primary/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-accent">
                      {post.category}
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex flex-col flex-1">
                    <p className="text-xs text-muted-foreground font-medium mb-3">{post.date}</p>
                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">{post.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">{post.excerpt}</p>
                    <div className="mt-auto text-xs font-bold text-primary flex items-center gap-1.5 group-hover:text-accent transition-colors uppercase tracking-wider w-fit">
                      Read Article
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="mt-12 text-center">
              <Link to="/resources">
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold px-8 rounded-none">
                  View All Resources
                </Button>
              </Link>
            </div>
          </div>
        </section>
        
        <section className="py-16 md:py-24 bg-secondary border-t border-primary/10 relative overflow-hidden" id="faq">
          <div 
            className="absolute inset-0 pointer-events-none opacity-[0.15]" 
            style={{ 
              backgroundImage: 'radial-gradient(hsl(var(--primary)) 1px, transparent 1px)', 
              backgroundSize: '24px 24px' 
            }} 
          />
          <div className="max-w-3xl mx-auto px-5 relative z-10">
            <div className="text-center mb-16">
              <span className="inline-block text-accent font-medium text-xs uppercase tracking-wider mb-3">Questions & Answers</span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">Questions</span></h2>
              <p className="text-muted-foreground">Everything you need to know about how we work and what to expect.</p>
            </div>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="bg-background border border-primary/30 px-6 data-[state=open]:border-accent transition-colors">
                  <AccordionTrigger className="text-left font-bold text-lg hover:no-underline hover:text-accent py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6 text-base">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-primary text-primary-foreground relative overflow-hidden" id="contact">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(184,151,90,0.15),transparent_50%)] pointer-events-none" />
          <div className="max-w-6xl mx-auto px-5 relative z-10 grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            
            <div className="text-left">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to fill your calendar?</h2>
              <p className="text-lg text-primary-foreground/80 mb-8 max-w-lg">
                Stop losing leads to bad follow-up and clunky websites. Claim your free growth audit and let's map out a system that books clients for you on autopilot.
              </p>
              
              <ul className="space-y-4 mb-12">
                {[
                  "Free 30-minute strategic growth audit",
                  "Custom roadmap tailored to your business",
                  "Zero obligation or aggressive sales pitches",
                  "Live system deployment in just 14 days"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-accent/20 flex items-center justify-center shrink-0 mt-0.5 border border-accent/30">
                      <Check className="w-4 h-4 text-accent" />
                    </div>
                    <span className="text-primary-foreground/90 font-medium">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="grid sm:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="font-bold text-lg mb-2 text-accent">Danugroup</h4>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">
                    30 N Gould St<br />
                    Sheridan, WY, 82801<br />
                    USA
                  </p>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2 text-accent">Isna Jean Louis</h4>
                  <p className="text-primary-foreground/80 text-sm leading-relaxed">
                    1240 E Normandy Blvd<br />
                    Deltona, FL, 32725<br />
                    USA
                  </p>
                </div>
              </div>

              <div className="w-full h-[250px] border border-primary/30 shadow-lg relative overflow-hidden bg-muted mt-8">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2861.168524419207!2d-106.95679908449673!3d44.79816508003668!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x5335fabc2a66677f%3A0x8f85ea0e70408220!2s30%20N%20Gould%20St%2C%20Sheridan%2C%20WY%2082801!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sheridan Office Map"
                  className="absolute inset-0 grayscale contrast-125 opacity-80"
                ></iframe>
              </div>
            </div>

            <div className="bg-background text-foreground p-8 md:p-10 border border-primary/30 shadow-2xl relative sticky top-24">
              <div className="absolute top-0 left-0 w-full h-1 bg-accent" />
              <h3 className="text-2xl font-bold mb-2">Claim Your Free Audit</h3>
              <p className="text-sm text-muted-foreground mb-6">Enter your details below and we'll be in touch within 2 hours.</p>
              
              <LeadForm />
            </div>

          </div>
        </section>
      </main>

      <Footer />

      <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <DialogContent className="max-w-xl bg-background border-primary/30 p-8 shadow-2xl">
          <DialogHeader className="mb-4 text-left">
            <DialogTitle className="text-2xl font-bold tracking-tight">Claim Your Free Audit</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter your details below and we'll be in touch within 2 hours.
            </DialogDescription>
          </DialogHeader>
          <LeadForm />
        </DialogContent>
      </Dialog>

      <Dialog open={showExitIntent} onOpenChange={setShowExitIntent}>
        <DialogContent className="max-w-xl bg-background border-primary/30 p-8 shadow-2xl">
          <DialogHeader className="mb-4 text-center">
            <div className="mx-auto w-16 h-16 bg-accent/20 text-accent flex items-center justify-center rounded-full mb-4">
              <Sparkles className="w-8 h-8" />
            </div>
            <DialogTitle className="text-3xl font-bold tracking-tight mb-2">Wait! Don't leave empty-handed.</DialogTitle>
            <DialogDescription className="text-muted-foreground text-lg">
              Get our free 5-step blueprint to double your inbound leads in 30 days.
            </DialogDescription>
          </DialogHeader>
          <LeadForm />
        </DialogContent>
      </Dialog>
    </div>
  );
}
